import React, {useEffect, useMemo, useState} from "react"
// MUI
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Skeleton,
    Stack,
    Typography,
} from "@mui/material"
import {AddRounded, DeleteOutlineRounded, Save, SubdirectoryArrowRightRounded} from "@mui/icons-material"
import {Grid2 as Grid} from "@mui/material"
// Layout
import {BuilderShell, FieldLabel, SectionCard} from "@/components/Layout"
// UI
import {CountrySelect, LanguageSelect, PresetChips} from "@/components/UI"
// Context
import {useData} from "@/context/PackagingDataProvider"
import {useErrorBoundary} from "@/context/ErrorBoundaryProvider"
import {usePermissions} from "@/context/PermissionsProvider"
// Utils
import {countryCodeToName, languageCodeToName} from "@/utils"
// Theme
import {packagingTokens as tk} from "@/theme/packagingTokens"
// Translation
import {useTranslation} from "react-i18next"

// Sentinel value of the brand select meaning "every brand I can manage".
// Not a UUID, so it can never collide with a real brand id.
export const ALL_BRANDS = "__all_brands__"
// Sentinel of the range select meaning "the brand-level preset" (no range
// override). A real value rather than "" so the select always shows a label.
export const ALL_RANGES = "__all_ranges__"

// Where a save goes. "brand" and "range" edit one stored list; "all" writes
// the same list onto every brand in scope (ranges keep their own overrides).
type Target = {kind: "all"} | {kind: "brand"; id: string} | {kind: "range"; id: string}

// A row as edited in the form. The selects work with option objects, so the
// stored two-letter codes are hydrated into them on load and read back out on
// save.
type PresetRow = {
    country: {code: string; label: string} | null
    language: {code: string; name: string} | null
}

const emptyRow = (): PresetRow => ({country: null, language: null})

const rowIsValid = (row: PresetRow) => !!row.country?.code || !!row.language?.code

// Codes are what the backend stores; it normalises case itself, but sending
// them already normalised keeps the dirty-check honest.
const rowToPreset = (row: PresetRow) => ({
    country: (row.country?.code || "").toUpperCase(),
    language: (row.language?.code || "").toLowerCase(),
})

const presetToRow = (preset: any): PresetRow => ({
    country: preset.country ? {code: preset.country, label: countryCodeToName(preset.country)} : null,
    language: preset.language ? {code: preset.language, name: languageCodeToName(preset.language)} : null,
})

export default function FiltersPropagation() {
    const {t} = useTranslation(["common"])
    const {
        brands, ranges,
        propagatedFilters,
        getBrandFilterPresets, saveBrandFilterPresets,
        getRangeFilterPresets, saveRangeFilterPresets, saveAllBrandsFilterPresets,
    } = useData()
    const {handleSuccess} = useErrorBoundary()
    const {isAccountRole} = usePermissions()

    // MEMBERs and ANALYSTs may see the propagation settings but not modify
    // them — the backend PUTs are gated to SUPERVISOR/ADMIN/OWNER. Negative
    // check so a standalone account with no org role keeps edit rights,
    // matching how the other tabs gate.
    const readOnly = isAccountRole(["member", "analyst"])

    const [brandUUID, setBrandUUID] = useState<string>("")
    // ALL_RANGES = all ranges of the brand, i.e. the brand-level preset itself.
    const [rangeUUID, setRangeUUID] = useState<string>(ALL_RANGES)
    const [rows, setRows] = useState<PresetRow[]>([])
    const [savedPresets, setSavedPresets] = useState<any[]>([])
    // Range target only: whether the loaded list is the brand's (no override).
    const [inherited, setInherited] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const [saving, setSaving] = useState<boolean>(false)
    // A failed presets load must not look like "no presets yet": saving over
    // it would wipe whatever failed to load. Editing stays blocked until a
    // retry succeeds.
    const [loadFailed, setLoadFailed] = useState<boolean>(false)
    const [reloadKey, setReloadKey] = useState<number>(0)
    const [confirmAllOpen, setConfirmAllOpen] = useState<boolean>(false)
    // Brands that already have a preset, for the "copy from" picker and the
    // synthesis. The provider refreshes it after every successful save.
    const presetSources: any[] = propagatedFilters?.brands ?? []

    const target: Target | null = useMemo(() => {
        if (!brandUUID) return null
        if (brandUUID === ALL_BRANDS) return {kind: "all"}
        if (rangeUUID && rangeUUID !== ALL_RANGES) return {kind: "range", id: rangeUUID}
        return {kind: "brand", id: brandUUID}
    }, [brandUUID, rangeUUID])

    // Preselect the first brand so the page is never empty for single-brand
    // accounts, which is the common case.
    useEffect(() => {
        if (!brandUUID && !!brands && brands.length > 0) setBrandUUID(brands[0].id)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [brands])

    // Ranges belong to one brand; the picker only ever lists the selected
    // brand's and is meaningless for "all brands".
    const rangesForBrand = useMemo(
        () =>
            !!ranges && brandUUID && brandUUID !== ALL_BRANDS
                ? ranges.filter((r: any) => r?.brand?.id === brandUUID)
                : [],
        [ranges, brandUUID],
    )
    const rangeSelectDisabled = !brandUUID || brandUUID === ALL_BRANDS

    const selectedBrandName = useMemo(
        () => (brands ?? []).find((b: any) => b.id === brandUUID)?.name ?? "",
        [brands, brandUUID],
    )

    const handleBrandChange = (value: string) => {
        setBrandUUID(value)
        // A range id only makes sense under the brand it was picked for.
        setRangeUUID(ALL_RANGES)
    }

    useEffect(() => {
        if (!target) return

        // Nothing to load for "all brands": the editor starts blank and what
        // is saved goes onto every brand.
        if (target.kind === "all") {
            setLoading(false)
            setLoadFailed(false)
            setInherited(false)
            setSavedPresets([])
            setRows([])
            return
        }

        let cancelled = false
        setLoading(true)
        setLoadFailed(false)
        setInherited(false)

        const load =
            target.kind === "range"
                ? getRangeFilterPresets(target.id).then((res: any) =>
                      res === null ? null : {presets: res.presets as any[], inherited: !!res.inherited},
                  )
                : getBrandFilterPresets(target.id).then((presets: any[] | null) =>
                      presets === null ? null : {presets, inherited: false},
                  )

        load.then((res: {presets: any[]; inherited: boolean} | null) => {
            if (cancelled) return
            // null = failed load (the provider already toasted); [] is a
            // genuine "no presets configured".
            if (res === null) {
                setLoadFailed(true)
                setSavedPresets([])
                setRows([])
                return
            }
            setInherited(res.inherited)
            setSavedPresets(res.presets)
            setRows(res.presets.map(presetToRow))
        }).finally(() => {
            if (!cancelled) setLoading(false)
        })

        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [target, reloadKey])

    const validRows = useMemo(() => rows.filter(rowIsValid), [rows])

    // Copying a brand's own preset onto itself is a no-op, so it is not offered
    // when the brand itself is the target. For a range, the parent brand's
    // preset is the most natural starting point for an override, so it stays.
    const copyableSources = useMemo(
        () => (target?.kind === "brand" ? presetSources.filter((s: any) => s.brand_id !== brandUUID) : presetSources),
        [presetSources, brandUUID, target],
    )

    // Loads another brand's preset into the editor. Nothing is persisted until
    // Save, so the copy can be adapted first.
    const copyFromBrand = (sourceBrandUUID: string) => {
        const source = copyableSources.find((s: any) => s.brand_id === sourceBrandUUID)
        if (!source) return
        setRows((source.presets ?? []).map(presetToRow))
    }

    const normalisedSaved = useMemo(
        () =>
            savedPresets.map((p: any) => ({
                country: (p.country || "").toUpperCase(),
                language: (p.language || "").toLowerCase(),
            })),
        [savedPresets],
    )

    // Dirty when the valid rows differ from what was loaded. Incomplete rows
    // are ignored so an in-progress row doesn't arm the Save button. For "all
    // brands" the baseline is what was last applied from this editor (empty on
    // entry), and an empty list is never offered (the backend rejects it — it
    // would clear every brand at once).
    const hasChanges = useMemo(() => {
        const differs = JSON.stringify(validRows.map(rowToPreset)) !== JSON.stringify(normalisedSaved)
        if (target?.kind === "all") return validRows.length > 0 && differs
        // An inherited range has no override to clear: saving an empty list
        // would be a no-op the backend answers with the brand's list again, so
        // deleting every inherited row is not a change to save.
        if (target?.kind === "range" && inherited && validRows.length === 0) return false
        return differs
    }, [target, inherited, validRows, normalisedSaved])

    const updateRow = (index: number, patch: Partial<PresetRow>) => {
        setRows((current) => current.map((row, i) => (i === index ? {...row, ...patch} : row)))
    }

    const removeRow = (index: number) => setRows((current) => current.filter((_, i) => i !== index))

    const addRow = () => setRows((current) => [...current, emptyRow()])

    // The backend de-duplicates too; doing it here keeps what-you-see-is-
    // what-saves — a duplicate row would otherwise silently vanish on the
    // next load.
    const presetsToSave = () => {
        const seen = new Set<string>()
        return validRows.map(rowToPreset).filter((p) => {
            const key = `${p.country}|${p.language}`
            if (seen.has(key)) return false
            seen.add(key)
            return true
        })
    }

    const handleSave = async () => {
        if (!target || readOnly || loadFailed) return
        // Writing onto every brand is confirmed first — it replaces each
        // brand's current list.
        if (target.kind === "all") {
            setConfirmAllOpen(true)
            return
        }
        setSaving(true)
        const presets = presetsToSave()
        if (target.kind === "range") {
            const res = await saveRangeFilterPresets(target.id, presets)
            if (res) {
                // The response carries what now applies: the range's own list,
                // or the brand's again when the override was cleared.
                setInherited(res.inherited)
                setSavedPresets(res.presets)
                setRows(res.presets.map(presetToRow))
                handleSuccess(t("success_saving_filter_presets"))
            }
        } else {
            const ok = await saveBrandFilterPresets(target.id, presets)
            if (ok) {
                setSavedPresets(presets)
                setRows(presets.map(presetToRow))
                handleSuccess(t("success_saving_filter_presets"))
            }
        }
        setSaving(false)
    }

    const handleConfirmAll = async () => {
        setConfirmAllOpen(false)
        setSaving(true)
        const presets = presetsToSave()
        const res = await saveAllBrandsFilterPresets(presets)
        if (res) {
            setSavedPresets(presets)
            setRows(presets.map(presetToRow))
            handleSuccess(t("success_saving_filter_presets_all", {updated: res.updated, unchanged: res.unchanged}))
        }
        setSaving(false)
    }

    const brandsCount = brands?.length ?? 0

    // Synthesis of what is already propagated: one row per brand with a
    // preset, its overriding ranges nested under it, and orphan overrides
    // (brand without a preset of its own) under a brand row with no chips.
    // Ordered like the brand select so the two read the same way.
    const synthesis = useMemo(() => {
        if (!propagatedFilters) return null
        const byBrand = new Map<string, {id: string; name: string; presets: any[]; ranges: any[]}>()
        for (const b of propagatedFilters.brands ?? []) {
            if (!b?.brand_id || !(b.presets?.length > 0)) continue
            byBrand.set(b.brand_id, {id: b.brand_id, name: b.brand_name, presets: b.presets, ranges: []})
        }
        for (const r of propagatedFilters.ranges ?? []) {
            if (!r?.range_id || !(r.presets?.length > 0)) continue
            if (!byBrand.has(r.brand_id)) byBrand.set(r.brand_id, {id: r.brand_id, name: r.brand_name, presets: [], ranges: []})
            byBrand.get(r.brand_id)!.ranges.push({id: r.range_id, name: r.range_name, presets: r.presets})
        }
        const order = new Map<string, number>((brands ?? []).map((b: any, i: number) => [b.id as string, i]))
        const rows = Array.from(byBrand.values()).sort(
            (a, b) => (order.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (order.get(b.id) ?? Number.MAX_SAFE_INTEGER),
        )
        return {
            rows,
            brandsCount: rows.filter((r) => r.presets.length > 0).length,
            rangesCount: rows.reduce((n, r) => n + r.ranges.length, 0),
        }
    }, [propagatedFilters, brands])

    // Clicking a synthesis row loads that target into the editor above.
    const jumpTo = (brandId: string, rangeId?: string) => {
        setBrandUUID(brandId)
        setRangeUUID(rangeId ?? ALL_RANGES)
    }

    const isCurrent = (brandId: string, rangeId?: string) =>
        target?.kind !== "all" && brandUUID === brandId && (rangeId ? rangeUUID === rangeId : rangeUUID === ALL_RANGES)

    const synthesisRowSx = (current: boolean) => ({
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        minHeight: 34,
        px: 1,
        borderRadius: tk.radius.sm,
        cursor: "pointer",
        backgroundColor: current ? tk.color.bg : "transparent",
        "&:hover": {backgroundColor: tk.color.bg},
    })

    const renderSynthesis = () => {
        // null = not loaded (or the load failed): say nothing rather than a
        // misleading "nothing propagated yet".
        if (!synthesis) return null
        return (
            <SectionCard
                title={t("filters_propagation_synthesis")}
                subtitle={
                    synthesis.rows.length === 0
                        ? t("filters_propagation_synthesis_hint")
                        : t("filters_propagation_synthesis_count", {brands: synthesis.brandsCount, ranges: synthesis.rangesCount})
                }
            >
                {synthesis.rows.length === 0 ? (
                    <Typography sx={{fontSize: "13px", color: tk.color.textSecondary}}>
                        {t("filters_propagation_synthesis_empty")}
                    </Typography>
                ) : (
                    <Box data-testid={"filters-synthesis"} sx={{maxHeight: 320, overflowY: "auto", mx: -1}}>
                        {synthesis.rows.map((row) => (
                            <Box key={row.id}>
                                <Box
                                    role={"button"}
                                    tabIndex={0}
                                    onClick={() => jumpTo(row.id)}
                                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); jumpTo(row.id) } }}
                                    sx={synthesisRowSx(isCurrent(row.id))}
                                >
                                    <Typography sx={{fontSize: "13px", fontWeight: 600, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: "0 1 220px"}}>
                                        {row.name}
                                    </Typography>
                                    {row.presets.length > 0 ? (
                                        <PresetChips presets={row.presets} />
                                    ) : (
                                        <Typography sx={{fontSize: "12px", color: tk.color.textTertiary, fontStyle: "italic"}}>
                                            {t("filters_propagation_synthesis_brand_none")}
                                        </Typography>
                                    )}
                                </Box>
                                {row.ranges.map((range) => (
                                    <Box
                                        key={range.id}
                                        role={"button"}
                                        tabIndex={0}
                                        onClick={() => jumpTo(row.id, range.id)}
                                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); jumpTo(row.id, range.id) } }}
                                        sx={{...synthesisRowSx(isCurrent(row.id, range.id)), minHeight: 30, pl: 2}}
                                    >
                                        <SubdirectoryArrowRightRounded sx={{fontSize: 14, color: tk.color.textTertiary, flexShrink: 0}} />
                                        <Typography sx={{fontSize: "12.5px", fontWeight: 500, color: tk.color.textSecondary, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: "0 1 198px"}}>
                                            {range.name}
                                        </Typography>
                                        <PresetChips presets={range.presets} />
                                    </Box>
                                ))}
                            </Box>
                        ))}
                    </Box>
                )}
            </SectionCard>
        )
    }

    const renderTargetSelector = () => (
        <SectionCard title={t("filters_propagation_target")}>
            <Grid container spacing={2} alignItems={"flex-start"}>
                <Grid size={{xs: 12, sm: 6}}>
                    <FormControl fullWidth size={"small"}>
                        <InputLabel id={"filters-propagation-brand-label"}>{t("brand")}</InputLabel>
                        <Select
                            labelId={"filters-propagation-brand-label"}
                            id={"filters-propagation-brand"}
                            label={t("brand")}
                            value={brandUUID}
                            onChange={(e) => handleBrandChange(e.target.value as string)}
                        >
                            {/* Only managers may write to every brand; for
                                read-only roles the entry would show an empty
                                editor with nothing to read. */}
                            {!readOnly && brandsCount > 1 && (
                                <MenuItem value={ALL_BRANDS} sx={{fontWeight: 600}}>
                                    {t("all_brands")}
                                </MenuItem>
                            )}
                            {!!brands &&
                                brands.map((brand: any) => (
                                    <MenuItem key={brand.id} value={brand.id}>
                                        {brand.name}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid size={{xs: 12, sm: 6}}>
                    <FormControl fullWidth size={"small"} disabled={rangeSelectDisabled}>
                        <InputLabel id={"filters-propagation-range-label"}>{t("range")}</InputLabel>
                        <Select
                            labelId={"filters-propagation-range-label"}
                            id={"filters-propagation-range"}
                            label={t("range")}
                            value={rangeSelectDisabled ? ALL_RANGES : rangeUUID}
                            onChange={(e) => setRangeUUID(e.target.value as string)}
                        >
                            <MenuItem value={ALL_RANGES}>{t("all_ranges")}</MenuItem>
                            {rangesForBrand.map((range: any) => (
                                <MenuItem key={range.id} value={range.id}>
                                    {range.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Typography sx={{fontSize: "12px", color: tk.color.textSecondary, mt: 0.75}}>
                        {rangeSelectDisabled ? t("filters_propagation_range_hint_disabled") : t("filters_propagation_range_hint")}
                    </Typography>
                </Grid>

                {/* Only offered once another brand actually has something to
                    copy — otherwise the control is noise. */}
                {!readOnly && copyableSources.length > 0 && (
                    <Grid size={{xs: 12, sm: 6}}>
                        <FormControl fullWidth size={"small"} disabled={loading}>
                            <InputLabel id={"filters-propagation-copy-label"}>{t("copy_presets_from")}</InputLabel>
                            <Select
                                labelId={"filters-propagation-copy-label"}
                                id={"filters-propagation-copy"}
                                label={t("copy_presets_from")}
                                value={""}
                                onChange={(e) => copyFromBrand(e.target.value as string)}
                            >
                                {copyableSources.map((source: any) => (
                                    <MenuItem key={source.brand_id} value={source.brand_id}>
                                        {source.brand_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Typography sx={{fontSize: "12px", color: tk.color.textSecondary, mt: 0.75}}>
                            {t("copy_presets_hint")}
                        </Typography>
                    </Grid>
                )}
            </Grid>
        </SectionCard>
    )

    const renderRow = (row: PresetRow, index: number) => (
        <Box
            key={index}
            sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                py: 2,
                borderBottom: index < rows.length - 1 ? `1px solid ${tk.color.borderLight}` : "none",
            }}
        >
            <Grid container spacing={2} sx={{flexGrow: 1}}>
                <Grid size={{xs: 12, sm: 6}}>
                    <CountrySelect
                        country={row.country}
                        setCountry={(val: any) => updateRow(index, {country: val})}
                        disabled={readOnly}
                        {...{t}}
                    />
                </Grid>
                <Grid size={{xs: 12, sm: 6}}>
                    <LanguageSelect
                        language={row.language}
                        setLanguage={(val: any) => updateRow(index, {language: val?.code ? val : null})}
                        disabled={readOnly}
                        {...{t}}
                    />
                </Grid>
            </Grid>
            {!readOnly && (
                <IconButton
                    aria-label={t("delete")}
                    onClick={() => removeRow(index)}
                    sx={{mt: 0.25, color: tk.color.textSecondary}}
                >
                    <DeleteOutlineRounded sx={{fontSize: 20}} />
                </IconButton>
            )}
        </Box>
    )

    const renderRows = () => {
        if (loading) {
            return (
                <Stack spacing={2}>
                    <Skeleton variant={"rounded"} height={40} />
                    <Skeleton variant={"rounded"} height={40} />
                </Stack>
            )
        }

        // A failed load gets its own state, never the empty one — the empty
        // state invites adding rows and saving, which would overwrite the
        // presets that failed to load.
        if (loadFailed) {
            return (
                <Stack spacing={1.5} alignItems={"flex-start"}>
                    <Typography sx={{fontSize: "13px", color: tk.color.textSecondary}}>
                        {t("filters_propagation_load_failed")}
                    </Typography>
                    <Button
                        variant={"outlined"}
                        size={"small"}
                        onClick={() => setReloadKey((k) => k + 1)}
                        sx={{textTransform: "none", fontWeight: 600, borderRadius: tk.radius.sm}}
                    >
                        {t("retry")}
                    </Button>
                </Stack>
            )
        }

        if (rows.length === 0) {
            return (
                <Typography sx={{fontSize: "13px", color: tk.color.textSecondary}}>
                    {target?.kind === "all" ? t("filters_propagation_empty_all") : t("filters_propagation_empty")}
                </Typography>
            )
        }

        return <Box>{rows.map(renderRow)}</Box>
    }

    // What the editor's hint says depends on where the save goes.
    const filtersHint = () => {
        if (target?.kind === "all") return t("filters_propagation_filters_hint_all", {count: brandsCount})
        if (target?.kind === "range") return t("filters_propagation_filters_hint_range")
        return t("filters_propagation_filters_hint")
    }

    // Range target: say whether the list on screen is the brand's or the
    // range's own, so an edit is understood as creating/keeping an override.
    const renderScopeNotice = () => {
        if (target?.kind === "range" && !loading && !loadFailed) {
            return (
                <Typography sx={{fontSize: "12.5px", color: tk.color.textSecondary, mb: 1.5}}>
                    {inherited
                        ? t("filters_propagation_inherited", {brand: selectedBrandName})
                        : t("filters_propagation_overridden", {brand: selectedBrandName})}
                </Typography>
            )
        }
        if (target?.kind === "all") {
            return (
                <Typography sx={{fontSize: "12.5px", color: tk.color.textSecondary, mb: 1.5}}>
                    {t("filters_propagation_all_brands_notice", {count: brandsCount})}
                </Typography>
            )
        }
        return null
    }

    return (
        <BuilderShell
            backTo={"/?tab=settings"}
            backLabel={t("back")}
            title={t("settings_filters_propagation")}
            subtitle={t("settings_filters_propagation_description")}
            maxWidth={920}
            actions={
                !readOnly && (
                    <Button
                        startIcon={<Save sx={{fontSize: 18}} />}
                        variant={"contained"}
                        disableElevation
                        disabled={!target || !hasChanges || saving || loading || loadFailed}
                        onClick={handleSave}
                        sx={{textTransform: "none", fontWeight: 600, borderRadius: tk.radius.sm, px: 2}}
                    >
                        {target?.kind === "all" ? t("filters_propagation_apply_all") : t("save")}
                    </Button>
                )
            }
        >
            <Stack spacing={3}>
                {renderTargetSelector()}

                <SectionCard
                    title={t("filters_propagation_filters")}
                    subtitle={filtersHint()}
                    action={
                        !readOnly && (
                            <Button
                                variant={"contained"}
                                disableElevation
                                startIcon={<AddRounded sx={{fontSize: 18}} />}
                                onClick={addRow}
                                disabled={!target || loading || loadFailed}
                                sx={{fontWeight: 600, borderRadius: tk.radius.sm}}
                            >
                                {t("add")}
                            </Button>
                        )
                    }
                >
                    {readOnly && (
                        <Typography sx={{fontSize: "12.5px", color: tk.color.textSecondary, mb: 1.5}}>
                            {t("filters_propagation_read_only")}
                        </Typography>
                    )}
                    {renderScopeNotice()}
                    <FieldLabel label={t("filters_propagation_rule")} />
                    {renderRows()}
                </SectionCard>

                {renderSynthesis()}
            </Stack>

            <Dialog open={confirmAllOpen} onClose={() => setConfirmAllOpen(false)} maxWidth={"sm"} fullWidth>
                <DialogTitle>{t("filters_propagation_apply_all_title")}</DialogTitle>
                <DialogContent>
                    <Typography>{t("filters_propagation_apply_all_body", {count: brandsCount})}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button sx={{color: "black", mr: 1}} onClick={() => setConfirmAllOpen(false)}>
                        {t("cancel")}
                    </Button>
                    <Button variant={"contained"} disableElevation onClick={handleConfirmAll}>
                        {t("confirm")}
                    </Button>
                </DialogActions>
            </Dialog>
        </BuilderShell>
    )
}
